import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { TrendUp, TrendDown, Minus, ArrowRight, Warning, MagnifyingGlass, Printer } from "@phosphor-icons/react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Sparkline } from "./Sparkline";
import { TREND_META, FLAG_META, fmtDate, daysSince } from "./utils";
import { formatCanonicalPercent } from "@/lib/goalAverage";
import PrintProgressReportDialog from "@/components/progress/PrintProgressReportDialog";

function StudentRow({ row, onSelect, isSelected, onToggleSelect }) {
  const navigate = useNavigate();
  const { student, goals, flags, last_data_date, active_goal_count } = row;
  const grade = student.grade;
  const days = daysSince(last_data_date);
  // Show top 2 goals as sparklines
  const topGoals = goals.slice(0, 2);
  return (
    <Card
      data-testid={`progress-row-${student.id}`}
      className="rounded-xl border border-[#E5E3DB] bg-white shadow-sm hover:shadow-md hover:border-[#8FA998]/40 transition-all cursor-pointer group"
      onClick={() => onSelect(student.id)}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {/* FP3-130 — Selection checkbox. Its own onClick stopPropagation
              prevents the surrounding row from opening Student Progress. */}
          <div
            className="pt-0.5 shrink-0"
            onClick={(e) => e.stopPropagation()}
            data-testid={`progress-select-wrap-${student.id}`}
          >
            <Checkbox
              checked={isSelected}
              onCheckedChange={(v) => onToggleSelect(student.id, !!v)}
              data-testid={`progress-select-${student.id}`}
              aria-label={`Select ${student.first_name} ${student.last_name}`}
            />
          </div>
          <div className="w-9 h-9 rounded-full bg-[#E8F0EB] text-[#1E3F33] flex items-center justify-center font-semibold heading-font text-sm shrink-0">
            {student.first_name?.[0]}
            {student.last_name?.[0]}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-semibold text-[#1F2937]">
                {student.first_name} {student.last_name}
              </p>
              <span className="text-[11px] text-[#6B7280]">Gr {grade}</span>
              <span className="text-[11px] text-[#6B7280]">&middot;</span>
              <span className="text-[11px] text-[#6B7280]">{active_goal_count} active goal{active_goal_count !== 1 ? "s" : ""}</span>
            </div>
            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
              {flags.length === 0 ? (
                <span className="text-[10px] text-[#6B7280]">No flags</span>
              ) : (
                flags.slice(0, 4).map((f) => {
                  const m = FLAG_META[f] || { label: f, text: "#6B7280", bg: "#F3F1EC" };
                  return (
                    <span
                      key={f}
                      data-testid={`flag-${f}`}
                      className="text-[9px] px-1.5 py-0.5 rounded font-medium"
                      style={{ color: m.text, backgroundColor: m.bg }}
                    >
                      {m.label}
                    </span>
                  );
                })
              )}
            </div>
            {/* Mini sparklines for top goals */}
            {topGoals.length > 0 && (
              <div className="mt-3 space-y-1.5">
                {topGoals.map((g) => {
                  const trendKey = g.computed_trend?.status || "no_data";
                  const meta = TREND_META[trendKey] || TREND_META.no_data;
                  // FP3-130A — display the canonical Goal Average
                  // (mean of per-session percentages). Falls back to
                  // "—" when the goal has no numeric session data
                  // yet — never the stale `current_mastery` snapshot.
                  const avgDisplay = formatCanonicalPercent(g.canonical_average);
                  return (
                    <div key={g.id} className="flex items-center gap-2">
                      <p className="text-[10px] text-[#6B7280] truncate flex-1 capitalize">{g.category}</p>
                      <Badge
                        className="text-[9px] px-1.5 py-0 h-4 font-medium border-0"
                        style={{ backgroundColor: meta.bg, color: meta.text }}
                      >
                        {meta.label}
                      </Badge>
                      <span
                        className="text-[10px] text-[#1F2937] font-semibold tabular-nums"
                        data-testid={`row-goal-avg-${g.id}`}
                        title={
                          g.canonical_average === null || g.canonical_average === undefined
                            ? "No data yet"
                            : `Average across ${g.canonical_session_count || 0} targeted session${(g.canonical_session_count || 0) === 1 ? "" : "s"}`
                        }
                      >
                        {avgDisplay}
                      </span>
                      <span className="text-[9px] text-[#9B9890]">/ {g.target_mastery}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="text-right shrink-0">
            <p className="text-[10px] text-[#6B7280]">Last data</p>
            <p className="text-xs font-medium text-[#1F2937]">{fmtDate(last_data_date)}</p>
            {days !== null && days > 14 && (
              <p className="text-[9px] text-[#B85C5C] mt-0.5">{days}d ago</p>
            )}
          </div>
          <ArrowRight size={14} className="text-[#6B7280] opacity-0 group-hover:opacity-100 transition-opacity self-center" />
        </div>
      </CardContent>
    </Card>
  );
}

export default function CaseloadProgress({ data, onSelectStudent }) {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all, attention, improving, stale, plateauing
  // FP3-130 — per-row selection state. Nothing selected → Print
  // Progress Summary runs against ALL current students in the
  // caseload; 1+ selected → runs against just those.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [printDialogOpen, setPrintDialogOpen] = useState(false);

  const rows = data?.students || [];
  const agg = data?.aggregate || {};

  const filtered = useMemo(() => {
    let list = rows;
    if (filter === "attention") {
      list = list.filter((r) => r.flags.some((f) => ["declining", "plateauing", "stale_data", "no_data"].includes(f)));
    } else if (filter === "improving") {
      list = list.filter((r) => r.flags.includes("improving") && !r.flags.includes("declining"));
    } else if (filter === "stale") {
      list = list.filter((r) => r.flags.includes("stale_data") || r.flags.includes("no_data"));
    } else if (filter === "plateauing") {
      list = list.filter((r) => r.flags.includes("plateauing"));
    } else if (filter === "declining") {
      list = list.filter((r) => r.flags.includes("declining"));
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((r) => `${r.student.first_name} ${r.student.last_name}`.toLowerCase().includes(q));
    }
    return list;
  }, [rows, search, filter]);

  // Selection helpers scoped to the current filtered view.
  const toggleSelect = (id, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };
  const selectAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const r of filtered) next.add(r.student.id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const selectionCount = selectedIds.size;

  const stats = [
    { key: "all", label: "Caseload", value: agg.total_students || 0, accent: "#1E3F33" },
    { key: "improving", label: "Improving", value: agg.improving_count || 0, accent: "#5A8A6A" },
    { key: "plateauing", label: "Plateauing", value: agg.plateauing_count || 0, accent: "#D9A05B" },
    { key: "declining", label: "Declining", value: agg.declining_count || 0, accent: "#B85C5C" },
    { key: "attention", label: "Need attention", value: agg.needs_attention_count || 0, accent: "#B85C5C" },
    { key: "stale", label: "Stale data", value: agg.stale_count || 0, accent: "#7D6340" },
  ];

  return (
    <div data-testid="caseload-progress">
      {/* Stats row — clickable filters */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-5">
        {stats.map((s) => (
          <button
            key={s.key}
            data-testid={`stat-${s.key}`}
            onClick={() => setFilter(s.key)}
            className={`text-left p-3 rounded-xl border transition-all ${
              filter === s.key
                ? "bg-white border-[#8FA998] shadow-sm"
                : "bg-[#F9F8F6] border-[#E5E3DB] hover:bg-white hover:border-[#d4d2cc]"
            }`}
          >
            <p className="text-[10px] text-[#6B7280] font-medium uppercase tracking-wide">{s.label}</p>
            <p className="text-2xl font-semibold heading-font tabular-nums mt-1" style={{ color: s.accent }}>
              {s.value}
            </p>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative mb-4 max-w-md">
        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
        <Input
          data-testid="progress-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search students..."
          className="pl-9 h-9 bg-white border-[#E5E3DB] rounded-lg text-sm"
        />
      </div>

      {/* FP3-130 — Selection toolbar. Compact single row above the
          list. Renders on ALL Progress tabs, tied to the current
          filtered view. */}
      <div
        className="mb-3 flex items-center gap-2 flex-wrap"
        data-testid="progress-selection-toolbar"
      >
        <span
          className="text-[11px] text-[#6B7280]"
          data-testid="progress-selection-count"
        >
          {selectionCount === 0
            ? `Nothing selected — Print covers all ${filtered.length} student${filtered.length === 1 ? "" : "s"}`
            : `${selectionCount} selected`}
        </span>
        <div className="flex-1" />
        <Button
          variant="outline"
          size="sm"
          onClick={selectAllVisible}
          disabled={filtered.length === 0}
          data-testid="progress-select-all"
          className="h-8 text-xs"
        >
          Select all
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={clearSelection}
          disabled={selectionCount === 0}
          data-testid="progress-clear-selection"
          className="h-8 text-xs"
        >
          Clear
        </Button>
        <Button
          size="sm"
          onClick={() => setPrintDialogOpen(true)}
          data-testid="progress-print-summary"
          className="h-8 text-xs bg-[#1E3F33] hover:bg-[#2A5544] text-white"
        >
          <Printer size={13} weight="bold" className="mr-1" />
          Print Progress Summary
        </Button>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 text-sm text-[#6B7280]">
          {filter === "all" ? "No students in caseload." : "No students match this filter."}
        </div>
      ) : (
        <div className="space-y-2.5">
          {filtered.map((row) => (
            <StudentRow
              key={row.student.id}
              row={row}
              onSelect={onSelectStudent}
              isSelected={selectedIds.has(row.student.id)}
              onToggleSelect={toggleSelect}
            />
          ))}
        </div>
      )}

      <PrintProgressReportDialog
        open={printDialogOpen}
        onOpenChange={setPrintDialogOpen}
        selectedIds={
          selectionCount > 0 ? Array.from(selectedIds) : []
        }
        totalStudents={filtered.length}
        hasSupervisedAssistants={!!data?.has_supervised_assistants}
      />
    </div>
  );
}
